/** @module csi/widget/_DialogMixin */
define([
	"dojo/_base/declare",
	"dojo/_base/event",
	"dojo/_base/lang",
	"dojo/dom-class",
	"dojo/dom-construct",
	"dojo/dom-style",
	"dojo/keys",
	"dijit/form/Button",
	"dijit/layout/ContentPane",
	"csi",
	"csi/_base/underscore",
	"csi/html",
	"csi/locale",
	"./_DialogValues"
], function(declare,event,lang,domClass,domConstruct,domStyle,keys,Button,ContentPane,csi,_,html,locale,_DialogValues) {

	return declare(null, {
		// Boolean: element that popped up dialog could be destroyed, so error on safe side
		refocus: false,

		// Boolean: indicates if dialog close button has been disabled/hidden
		bRestrictClose: false,

		// Boolean: indicates whether or not to add save/cancel buttons
		dialogActions: false,

		// set to true if you want to destroy the dialog upon hiding
		destroyOnHide: false,

		// Boolean: indicates if cancel button should be visible (only if dialogActions == true )
		showCancelButton: true,

		// Boolean: Submit button to be primary button
		submitButtonToBePrimary: true,

		// DOMNode: explicit form for dialog - this will take over form implicit functionality of dialog
		form: null,

		// Button: Store reference to submit button for dialog.
		submitButton: null,

		// Button: Store reference to cancel button for dialog.
		cancelButton: null,

		// String: introduction message for dialog that shows under title bar
		//intro: "",

		// DOMNode: location for displaying introduction message
		//introNode: null,

		// process (internalize/externalize) values on set and get
		processValues: false,

		// String: localization resource to use for submit button
		submitResource: "Save",

		// String: localization resource to use for cancel button
		cancelResource: "Cancel",

		// store initial values in case they are needed
		_initialValues: null,

		// adds ablity to add more action items while creating the dialog
		onRenderDialogActions: function(/*domNode*/dom) {},

		// Function: extension point for when dialog is first created
		onCreate: function() {},

        // Function: extension point for when dialog is started up
        onStartup: function() {},

		// Function: extension point for when dialog is destroyed
		onDestroy: function() {},

		// Function: extension point for when dialog is saved. Return exactly 'false' if the saving was not successful.
		onSave: function(/*Object*/values) {},

		// Function: extension point for when dialog is cancelled
		onCancel: function() {},


		createIntroNode: function(/*_WidgetBase?*/content) {
			// summary: create intro node for dialog
			if (!this.introNode) {
				this.introNode = new ContentPane({
					"class": this.baseClass + "Intro",
					content: content
				});

				this.addChild(this.introNode, 0);
			}

			return this.introNode;
		},

		destroy: function() {
			// summary: call onDestroy extension point
			this.onDestroy();
			this.inherited(arguments);
		},

		postCreate: function() {
			this.inherited(arguments);

			if (this.form) {
				// if explicit form is specified, then set as content
				this.set("content", this.form);
				this._onCreate();
			} else if (!this.href) {
				// if dialog is not loaded from external file, then finish creation here
				this._onCreate();
			}

			// hide close button if restricted
			if (this.bRestrictClose && this.closeButtonNode) {
				domClass.add(this.closeButtonNode, "nodisplay");
			}
		},

		startup: function() {
		    this.inherited(arguments);
		    this.onStartup();
		},

		/**
		 * Call when user needs to resume using dialog after clicking Save
		 */
		resumeAfterSave: function() {
			this._enableDialogActions(true);
		},

		onHide: function() {
			// summary: destroy dialog after dialog is hidden
			if (this.destroyOnHide) {
				this.destroyRecursive();
			}
		},

		onLoad: function() {
			this.inherited(arguments);

			// finish creation after loading external file
			this._onCreate();

			// set values on setup since the widgets were not created when dialog was first created
			if (this._initialValues) {
				this.set("value", this._initialValues);
			}
		},

		_onCreate: function() {
			// style dialog with default class
			this.onCreate();

			// add Save/Cancel actions if directed
			if (this.dialogActions) {
				var	div = domConstruct.create("div", {"class":"csiActionsNode"}, this.containerNode);
				div = domConstruct.create("div", {"class":"csiActions"}, div);

				this.submitButton = new Button({
					"class": html.CSS_ACTION + " " + (this.submitButtonToBePrimary ? html.CSS_BUTTON_PRIMARY : html.CSS_BUTTON_LINK),
					label: _.escape(locale.resource(this.submitResource)),
					type: "submit"
				}).placeAt(div);

				if (this.showCancelButton) {
					this.cancelButton = new Button({
						"class": html.CSS_ACTION + " " + (this.submitButtonToBePrimary ? html.CSS_BUTTON_LINK : html.CSS_BUTTON_SECONDARY),
						label: _.escape(locale.resource(this.cancelResource)),
						onClick: lang.hitch(this, "onCancel")
					}).placeAt(div);
				}

				// adds ability to add more action items
				this.onRenderDialogActions(div);
			}
		},

		_onKey: function(/*Event*/evt) {
			// summary: if restricting the close button, then also trap the escape key
			if (this.bRestrictClose && (evt.charOrCode == keys.ESCAPE)) {
				return;
			}

			this.inherited(arguments);
		},

		/**
		 * Make sure that dialog actions are enabled when the dialog is shown, in cases where dialog is not destroyed when hidden
		 */
		_onShow: function(){
			if (this.dialogActions) {
				this._enableDialogActions(true);
			}

			this.inherited(arguments);
		},

		_onSubmit: function(/*Event*/e) {
			// summary: call onSave if values are valid, and if they changed - only do that check though
			// when using normal dialog Save/Cancel buttons

			// DE7472 - for some reason space bar was triggering submit twice - check below will stop that
			if (this.open !== undefined && !this.open) return;
			if (!this.validate()) return;

			var value = this.get("value");

			this._enableDialogActions(false);

			if (!this.dialogActions || this._hasValueChanged(this._initialValues, value)) {
				if (this.onSave(value) === false) {
					this.resumeAfterSave();
				}
			} else {
				this.onCancel();
			}

			event.stop(e);
		},

		/**
		 * Manage enabled status of dialog action buttons
		 * @param {boolean} bEnable
		 */
		_enableDialogActions: function(bEnable) {
			html.setDisabled(this.submitButton, !bEnable);
			html.setDisabled(this.cancelButton, !bEnable);
		},

		_hasValueChanged: function(/*Object*/v1, /*Object?*/v0) {
			// summary: default is simple check on each element of value
			return v0 ? !csi.objectEquals(v1, v0) : !!v1;
		},

		_getValueAttr: function() {
			// summary: custom getter for the dialog value
			var	obj = this.form ? this.form.get("value") : this.inherited(arguments);
			return this.processValues ? _DialogValues.externalizeValues(obj, this) : obj;
		},

		_setIntroAttr: function(/*String*/intro) {
			// summary: set introduction message for dialog
			if (intro) {
				if (this.introNode) {
					html.clear(this.introNode.domNode);
				} else {
					this.createIntroNode();
				}

				html.multiLineText(this.introNode.domNode, intro);
			} else if (this.introNode) {
				this.removeChild(this.introNode);
				this.introNode = null;
			}
		},

		_setValueAttr: function(/*Object*/obj) {
			// summary: custom setter for the dialog value
			this._initialValues = csi.deepCopy(obj);

			// internalizeValues only works once loaded, so in that case wait to set values until later
			if (this.isLoaded || !this.processValues) {
				if (this.processValues) {
					obj = _DialogValues.internalizeValues(obj, this);
				}

				if (this.form) {
					this.form.set("value", obj);
				} else {
					this.inherited(arguments);
				}
			}
		}
	});
});